import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useMemo } from 'react';

import {
  ClientSideRowModelModule,
  DateFilterModule,
  ModuleRegistry,
  PaginationModule,
  type ColDef,
  type ICellRendererParams,
} from '@mint-lib/ag-grid/community';
import { useTranslation } from '@mint-lib/i18n';
import { Locked } from '@mint-lib/icons';
import { Button, TruncateText } from '@mint-lib/tw-components';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';

import { dateFilterParams } from '../../../helpers/ag-grid-helpers.js';
import type { PrivateBucket } from '../../../helpers/knowledge-base/fileSystemListing.js';
import { KnowledgeBaseNoRowOverlay } from '../RagTable/NoRowsOverlay.jsx';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  PaginationModule,
  DateFilterModule,
]);

const DESCRIPTION_PLACEHOLDER = '-';

type NameCellProps = ICellRendererParams<PrivateBucket> & {
  onOpenBucket: (bucketId: string) => void;
};

const NameCell = ({ data, onOpenBucket }: NameCellProps) =>
  data && (
    <div className="flex h-full w-full flex-row items-center">
      <Button
        variant="plain"
        size="small"
        className="w-full justify-start text-text-primary hover:text-text-primary"
        data-dd-action-name="Knowledge base: open user private folder"
        onClick={() => onOpenBucket(data.id)}
      >
        <Locked className="flex shrink-0 fill-icon-primary" />
        <TruncateText>{data.name}</TruncateText>
      </Button>
    </div>
  );

const CreationDateCell = ({ data }: ICellRendererParams<PrivateBucket>) =>
  data?.createdAt ? <StaticDateSpan date={data.createdAt} /> : null;

type Props = {
  buckets: PrivateBucket[];
  onOpenBucket: (bucketId: string) => void;
};

const UsersPrivateFoldersTable = ({ buckets, onOpenBucket }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const columnDefs = useMemo<ColDef<PrivateBucket>[]>(
    () => [
      {
        headerName: t('Name'),
        field: 'name',
        filter: true,
        flex: 2,
        cellRenderer: NameCell,
        cellRendererParams: { onOpenBucket },
      },
      {
        headerName: t('Description'),
        colId: 'description',
        flex: 2,
        sortable: false,
        valueGetter: ({ data }) => data?.description || DESCRIPTION_PLACEHOLDER,
      },
      {
        headerName: t('Creation date'),
        field: 'createdAt',
        flex: 1,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        cellRenderer: CreationDateCell,
      },
    ],
    [t, onOpenBucket],
  );

  return (
    <div
      className="min-h-0 w-full flex-1"
      data-testid="users-private-folders-table"
    >
      <AgGridReact
        rowData={buckets}
        columnDefs={columnDefs}
        getRowId={({ data }) => data.id}
        rowModelType="clientSide"
        loadThemeGoogleFonts
        paginationAutoPageSize
        pagination
        suppressMovableColumns
        suppressContextMenu
        components={{
          agNoRowsOverlay: KnowledgeBaseNoRowOverlay,
        }}
      />
    </div>
  );
};

export { UsersPrivateFoldersTable };
